import _ from 'lodash';
import Mustache from 'mustache';
import { LineItem as LineItemCore } from '../core';
import type { LineItem } from '../types';
import { PALLET_SSCC_VARIABLES } from './palletSscc';

/**
 * The "Pallets" section a pick ticket prints for an organization with pallet-sscc on.
 *
 *   flag OFF ─────────────────────────────────▶ nothing appended; the template prints as written
 *   flag ON ─▶ template uses palletNumber/sscc ─▶ nothing appended; its own columns carry them
 *          └─▶ otherwise ─▶ one row per pallet: Pallet # · SSCC · items · lines (MIXED marked)
 */
export type PalletSectionRow = { number: string; sscc: string; items: string; lineCount: number; isMixed: boolean };

type PalletSectionSource = {
  lineItems: readonly LineItem[];
  organization?: { productDescriptionTemplate?: string | null } | null;
};

const PALLET_VARIABLE_REFERENCE = new RegExp(
  `\\{\\{[{#^/&]?\\s*(?:[\\w.]+\\.)?(?:${PALLET_SSCC_VARIABLES.join('|')})\\s*\\}`
);

const palletNumberCollator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

const isPalletSectionSource = (data: unknown): data is PalletSectionSource =>
  typeof data === 'object' && data !== null && 'lineItems' in data && Array.isArray(data.lineItems);

export const referencesPalletVariables = (pages: readonly { html?: string }[]): boolean =>
  pages.some((page) => PALLET_VARIABLE_REFERENCE.test(page.html ?? ''));

export function buildPalletSectionRows({ lineItems, organization }: PalletSectionSource): PalletSectionRow[] {
  const palletized = _.uniqBy(
    lineItems.filter((lineItem) => !!lineItem.pallet),
    (lineItem) => lineItem.id
  );

  return Object.values(_.groupBy(palletized, (lineItem) => lineItem.pallet?.id))
    .map((onePallet) => ({
      number: onePallet[0]?.pallet?.number ?? '',
      sscc: onePallet[0]?.pallet?.sscc ?? '',
      items: _.uniq(
        onePallet.map((lineItem) => LineItemCore.productDescription(lineItem, organization?.productDescriptionTemplate))
      ).join(', '),
      lineCount: onePallet.length,
      isMixed: _.uniq(onePallet.map((lineItem) => lineItem.productId ?? lineItem.product?.id)).length > 1,
    }))
    .sort((left, right) => palletNumberCollator.compare(left.number, right.number));
}

const CELL = (width: string) => `display: flex; flex-direction: column; width: ${width}; padding-right: 4px;`;
const TEXT = 'font-size: 9px; color: rgb(71, 71, 70);';
const HEADER = 'font-size: 9px; font-weight: 600;';

/** Only the CSS main's default templates use, so react-pdf-html renders it: flex divs, px sizes, weights 500/600. */
const PALLET_SECTION_TEMPLATE = `
<div class="pick-ticket-pallets" data-testid="pick-ticket-pallets" style="display: flex; flex-direction: column; margin: 16px 24px 0px 24px;">
  <div style="display: flex; flex-direction: row; padding-bottom: 4px;"><span style="font-size: 11px; font-weight: 600;">Pallets</span></div>
  <div style="display: flex; flex-direction: row; background-color: #f7f8f3; padding: 4px;">
    <div style="${CELL('15%')}"><span style="${HEADER}">Pallet #</span></div>
    <div style="${CELL('30%')}"><span style="${HEADER}">SSCC</span></div>
    <div style="${CELL('45%')}"><span style="${HEADER}">Items</span></div>
    <div style="${CELL('10%')}"><span style="${HEADER}">Lines</span></div>
  </div>
  {{#pallets}}
  <div class="pick-ticket-pallet-row" style="display: flex; flex-direction: row; border-top: 1px solid #E0E0E0; padding: 4px;">
    <div style="${CELL('15%')}"><span style="${TEXT}">{{number}}{{#isMixed}} MIXED{{/isMixed}}</span></div>
    <div style="${CELL('30%')}"><span style="${TEXT}">{{sscc}}</span></div>
    <div style="${CELL('45%')}"><span style="${TEXT}">{{items}}</span></div>
    <div style="${CELL('10%')}"><span style="${TEXT}">{{lineCount}}</span></div>
  </div>
  {{/pallets}}
</div>`;

/** Empty unless the order has pallets and the template does not print them itself. */
export function renderPalletSection(pages: readonly { html?: string }[], data: unknown): string {
  if (!isPalletSectionSource(data) || referencesPalletVariables(pages)) {
    return '';
  }

  const pallets = buildPalletSectionRows(data);

  return pallets.length > 0 ? Mustache.render(PALLET_SECTION_TEMPLATE, { pallets }) : '';
}
